import { Injectable } from "@angular/core";
import moment from "moment";
import { MediaShow, Show } from "./show";
import { UserService } from "./user-service";
import { ShowRepository } from "./show-repository";
import { HttpService } from "../infrastructure/http/http";
import {
  CalendarSearchResult,
  CalendarSearchService,
} from "../components/calendar/services/calendar-search.service";
import { Subject, Subscription } from "rxjs";
import { Channel, EAutoExtendLiveShowBehaviour } from "./channel";
import { User } from "./user";

@Injectable()
export class CalendarService {
  private _lastSearchResult: Show[];
  private _next484oursResult: Show[];
  private _next484oursCacheChannelId: number;
  private _interval;
  private _currentIndex: number;
  private _emptyDuration: moment.Duration;
  private _currentTime: moment.Moment;
  private _currentDate: moment.Moment;
  private _today: moment.Moment;
  private showsCountNextDropdown = 20;
  private updatingShows = false;
  private duringSkip = false;
  private _currentShow: Show;
  private _clinetId: string;

  public currentShowChanged = new Subject<void>();
  public cacheUpdated = new Subject<void>();
  public clear48HoursCacheOnShowChanged = true;
  private _currentShowAboutToEnd: string;
  autoExtendLiveShowBehaviour: EAutoExtendLiveShowBehaviour;
  currentChannel: Channel;

  public set currentShow(show: Show) {
    if (show !== this._currentShow) {
      this._currentShow = show;
    }

    this.currentShowChanged.next();
  }
  public get currentShow(): Show {
    return this._currentShow;
  }
  nextShows: Show[] = [];
  currentUser: User;
  nextPrerollShows: Show[] = [];
  private searchSubscription: Subscription;
  changeCurrentDate: Subject<moment.Moment>;

  constructor(
    private userService: UserService,
    private httpService: HttpService,
    private showRepository: ShowRepository,
    private calendarSearchService: CalendarSearchService,
  ) {
    this.duringSkip = false;
    this._today = moment(new Date()).startOf("day");
    this._currentDate = this._today.clone();
    this._emptyDuration = moment.duration(0, "minutes");
    this._currentTime = moment(new Date());

    this.userService.getCurrentChannel().then(async (channel) => {
      if (channel) {
        this.currentChannel = channel;
        const channelInfo = await this.userService.getCurrentChannelInfo(false);
        this.autoExtendLiveShowBehaviour =
          channelInfo.autoExtendLiveShowBehaviour;
        this.currentUser = this.userService.getCurrentUser();
        if (this.currentUser && channel.timezoneId > 0) {
          const intervalFunc = () => {
            this._currentTime = moment();
            this._currentTime.add(1, "seconds");
            //console.log('calendar-service->intervalFunc', this.currentShow);
            this.updateShows().then();
          };
          intervalFunc();
          this._interval = setInterval(intervalFunc, 1000);
          this.updateShows().then();
        }
      }
    });
    this.changeCurrentDate = new Subject<moment.Moment>();
    this.calendarSearchService.resultUpdate.subscribe(
      this.onSearchResultsUpdated.bind(this),
    );
    this.calendarSearchService.termUpdate.subscribe(
      this.onCalendarSearchTermUpdate.bind(this),
    );

    this.showRepository.onShowsChange.subscribe(() => {
      if (this.clear48HoursCacheOnShowChanged) {
        this.clear48HoursCache();
      }
    });
  }

  get CurrentTime(): moment.Moment {
    return this._currentTime;
  }

  get DuringSkip() {
    return this.duringSkip;
  }

  getByDates(
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    shouldIncludeSecEvents?: boolean,
    abortSubject?: Subject<void>,
    includeLiveFeedLatencies = false,
    logResponse = false,
  ): Promise<Show[]> {
    return this.showRepository
      .getByDates(
        channelId,
        startDate,
        endDate,
        shouldIncludeSecEvents,
        abortSubject,
        includeLiveFeedLatencies,
        logResponse,
      )
      .then((result) => {
        this._lastSearchResult = result;

        return this._lastSearchResult;
      });
  }

  /*async updateShowsCache() {
        await this.update24HoursShowsCache();
    }*/

  /*async update24HoursShowsCache(shows?: Show[]) {
      if (!shows) {
        this.updatingShows = true;
        shows = await this.showRepository.getByDates(this._today, this._today.clone().add(1, 'days'), true);
        this.updatingShows = false;
      }
        if (this.duringSkip) { this.duringSkip = false; }
        this._24HoursCache = shows;
        this.updateShows();
        return shows;
    }*/

  async updateShows() {
    if (
      !this._lastSearchResult || // service not fully initilized
      !this._currentTime || // service not fully initilized
      this.updatingShows || // fetching shows from server
      this.duringSkip // skip to show is in progress
    ) {
      return;
    }

    let i = 0;
    const currentChannel = await this.userService.getCurrentChannel();
    this.currentChannel = currentChannel;
    let relevantCache = this._lastSearchResult;

    if (currentChannel.isBlock === false) {
      relevantCache = await this._getNext48HoursCache(currentChannel);
    }

    if (
      this.currentShow &&
      relevantCache.indexOf(this.currentShow) === this._currentIndex
    ) {
      i = this._currentIndex;
    }

    this.currentShow = null;
    let nextShows: Show[] = [];
    this.nextShows = relevantCache;
    if (currentChannel.isBlock === false) {
      if (relevantCache && relevantCache.length > 0) {
        loopShows: for (; i < relevantCache.length; i++) {
          const show = relevantCache[i];
          switch (this.getShowPlayingState(show.startTime, show.duration)) {
            case "current":
              this._currentIndex = i;
              this.currentShow = show;
              nextShows = this.prepareNextShowsList(i + 1, relevantCache);
              break loopShows;
            case "future":
              nextShows = this.prepareNextShowsList(i, relevantCache);
              break loopShows;
          }
        }
      }
      this.nextShows = nextShows;
      this.prepareNextPrerollShowsList();
    }
  }
  prepareNextPrerollShowsList(): void {
    if (this.currentUser && this.nextShows) {
      const relevantPrerollShowDurationMs =
        this.currentUser.channelPlaylistDurationMs -
        this.currentUser.channelPlaylistRequestAheadMs;
      const prerollPlaylistEndTime = moment
        .utc()
        .clone()
        .add(relevantPrerollShowDurationMs, "milliseconds");
      this.nextPrerollShows = this.nextShows.filter((s) =>
        s.startTime.isSameOrBefore(prerollPlaylistEndTime),
      );
    }
  }

  prepareNextShowsList(index: number, dataSource: Show[]): Show[] {
    let showsLeft = dataSource
      .slice(index)
      .filter((s) => !(s instanceof MediaShow) || !(s as MediaShow).isContract);
    if (showsLeft.length >= this.showsCountNextDropdown) {
      showsLeft = showsLeft.slice(0, this.showsCountNextDropdown);
    } else {
      const sevenDaysFromNow = this._currentTime.clone().add(7, "days");
      for (let i = 0; i < showsLeft.length; i++) {
        if (showsLeft[i].startTime > sevenDaysFromNow) {
          showsLeft = showsLeft.slice(0, i);
        }
      }
    }
    return showsLeft;
  }

  getShowPlayingState(start: moment.Moment, duration: moment.Duration): string {
    const past = start.clone().isSameOrBefore(this._currentTime);
    const future = start.clone().add(duration).isSameOrAfter(this._currentTime);

    if (future) {
      if (past) {
        return "current";
      }
      return "future";
    }
    return "past";
  }

  async skipCurrentShow(nextShowId: string, clientId: string, offsetSecs = 0) {
    if (!nextShowId) {
      return;
    }
    const id = this.currentShow?.id;
    this.duringSkip = true;
    this.nextShows = [];
    /*if (
      this.currentChannel.isLiveLocked &&
      this.autoExtendLiveShowBehaviour ==
        EAutoExtendLiveShowBehaviour.EndlesslyExtendLiveShow
    ) {
      console.log(`CalendarService->skipCurrentShow-> unlocking the live`);
      try {
        await this.showRepository.updateLiveBroadcastStatus(
          this.currentChannel.id,
          false,
        );
      } catch (error) {
        console.error(
          `CalendarService->skipCurrentShow-> no need to unlock based on current show`,
          error,
        );
      }
    }*/

    return this.httpService
      .post("Scheduler/SkipToShow", {
        CurrentShowId: id,
        NextShowId: nextShowId,
        ClientId: clientId,
      })
      .then(() => {
        this.showRepository.onShowsChange.next();
      })
      .catch((error) => {
        console.error(
          `CalendarService->skipCurrentShow-> error skipping to show`,
          error,
        );
      })
      .finally(() => {
        this.duringSkip = false;
        if (
          this.autoExtendLiveShowBehaviour ==
          EAutoExtendLiveShowBehaviour.EndlesslyExtendLiveShow
        ) {
          /*setTimeout(async () => {
            await this.showRepository.updateLiveBroadcastStatus(
              this.currentChannel.id,
              true,
            );
          }, 10000);*/
        }
      });
  }

  onSkipToShowSelectedUpdated(showId: string, clientId: string): void {
    this.duringSkip = true;
    this.userService
      .getCurrentChannel()
      .then((channel) => {
        this.currentChannel = channel;
        const currentShowId = this.currentShow?.id;
        return this.showRepository.updatePrerollMedia(
          channel.id,

          [showId],
          currentShowId,
          clientId,
        );
      })
      .then(async () => {
        this.duringSkip = false;
        this.showRepository.onShowsChange.next();
        const channelInfo = await this.userService.getCurrentChannelInfo(false);
        this.autoExtendLiveShowBehaviour =
          channelInfo.autoExtendLiveShowBehaviour;
      })

      .catch(() => {
        this.duringSkip = false;
      });
  }

  private getRelevantCache() {
    return this._lastSearchResult;
  }
  private async _getNext48HoursCache(channel: Channel) {
    if (
      !this._next484oursResult ||
      this._next484oursCacheChannelId !== channel.id
    ) {
      console.log(
        "calendar-service-> this.showRepository.getByDates for channel",
        channel.id,
      );
      this._next484oursResult = await this.showRepository.getByDates(
        channel.id,
        this._currentTime.clone(),
        this._currentTime.clone().add(48, "hours"),
      );
      this._next484oursCacheChannelId = channel.id;
      this.cacheUpdated.next();
    }
    return this._next484oursResult;
  }
  private clear48HoursCache() {
    this._next484oursResult = null;
  }

  getCurrentShowElapsedTime(): moment.Duration {
    if (this.currentShow) {
      const seconds =
        this._currentTime.unix() - this.currentShow.startTime.unix();
      if (seconds < this.currentShow.duration.asSeconds()) {
        return moment.duration(seconds, "seconds");
      }
    }
    return this._emptyDuration;
  }
  getCurrentShowRemainingTime(): moment.Duration {
    if (this.currentShow && this.currentShow.duration) {
      const elapsedSeconds =
        this._currentTime.unix() - this.currentShow.startTime.unix();
      if (elapsedSeconds < this.currentShow.duration.asSeconds()) {
        const remainingTime = this.currentShow.duration
          .clone()
          .subtract(elapsedSeconds, "seconds");
        // if (
        //   remainingTime.asSeconds() < 50 &&
        //   remainingTime.asSeconds() > 0 &&
        //   this._currentShowAboutToEnd !== this.currentShow.id
        // ) {
        //   this._currentShowAboutToEnd = this.currentShow.id;
        //   this.clear48HoursCache();
        //   this.updateShows();
        //   setTimeout(() => {
        //     this._currentShowAboutToEnd = "";
        //   }, 10000);
        // }
        return remainingTime;
      }
    }
    return this._emptyDuration;
  }

  // getCurrentShowRemainingTime(): moment.Duration {
  //   if (this.currentShow && this.currentShow.duration) {
  //     const elapsedSeconds =
  //       this._currentTime.unix() - this.currentShow.startTime.unix();
  //     if (elapsedSeconds < this.currentShow.duration.asSeconds()) {
  //       return this.currentShow.duration
  //         .clone()
  //         .subtract(elapsedSeconds, "seconds");
  //     }
  //   }
  //   return this._emptyDuration;
  // }

  clearCache(clearAll = false): void {
    this._next484oursResult = null;
    if (clearAll) {
      this._currentIndex = null;
      this.currentShow = null;
      this.nextShows = [];
    }
  }

  onCalendarSearchTermUpdate() {
    console.log("CalendarService->onCalendarSearchTermUpdate");
    this.calendarSearchService.resetResults();
    if (this._lastSearchResult) {
      this._lastSearchResult.forEach((show) => {
        this.calendarSearchService.updateResult(
          show.id,
          this.calendarSearchService.testSearchTermMatch(show.name),
        );
      });
    }

    this.calendarSearchService.updateObservers();
  }

  getShowStartTime(showId: string): moment.Moment {
    const show = this.getRelevantCache()?.find((s) => s.id === showId);
    return show ? show.startTime : null;
  }

  getShowById(showId: string): Show {
    return this.getRelevantCache()?.find((show) => show.id === showId);
  }

  onSearchResultsUpdated(searchResults: CalendarSearchResult): moment.Moment {
    console.log(
      "CalendarService->onSearchResultsUpdated,searchResults",
      searchResults,
    );
    if (!searchResults || searchResults.matches === 0) {
      return;
    }
    let closestShow: Show;
    if (
      this.currentShow &&
      searchResults.resultsMatch.indexOf(this.currentShow.id) > -1
    ) {
      closestShow = this.currentShow;
    } else {
      const now = moment().valueOf();
      closestShow = this.getRelevantCache()?.find(
        (s) =>
          s.startTime.valueOf() > now &&
          searchResults.resultsMatch.indexOf(s.id) > -1,
      );
    }
    if (closestShow) {
      this.calendarSearchService.moveToSearchResult(
        searchResults.resultsMatch.indexOf(closestShow.id),
      );
      this.changeCurrentDate.next(closestShow.startTime);
    }
  }
  getShowFromCache(showUid: string): Show | null {
    return this.nextShows?.find((s) => s.id === showUid) ?? null;
  }
}
